import { useCallback, useMemo } from 'react';
import { rage } from '~/rage';
import { useObservable } from '~/rage/reactHooks';
import { RewardType } from '~/shared';

interface Option {
	value: string;
	label: string;
}

export const useRewardOptions = () => {
	const vehicleModels = useObservable(rage.vehicles.getVehicleModels());
	const lootboxes = useObservable(rage.cases.getLootboxes());
	const bundles = useObservable(rage.cases.getBundles());
	const inventoryItems = useObservable(rage.inventory.getInventoryItems());
	const gameConfigs = useObservable(rage.gameConfig.getConfigs());

	const vehicleOptions = useMemo(() => {
		if (!vehicleModels) return [];
		return vehicleModels.map((v) => ({
			value: v.model,
			label: v.name,
		}));
	}, [vehicleModels]);

	const lootboxOptions = useMemo(() => {
		if (!lootboxes) return [];
		return lootboxes.map((c) => ({
			value: c.id,
			label: c.name,
		}));
	}, [lootboxes]);

	const bundleOptions = useMemo(() => {
		if (!bundles) return [];
		return bundles.map((b) => ({
			value: b.id,
			label: b.name,
		}));
	}, [bundles]);

	const itemOptions = useMemo(() => {
		if (!inventoryItems) return [];
		return inventoryItems.map((i) => ({
			value: i.id,
			label: i.name,
		}));
	}, [inventoryItems]);

	const getTuningKitOptions = useCallback(
		(vehicleModel: string) => {
			if (!gameConfigs?.vehicleTuning) return [];
			return gameConfigs.vehicleTuning.itemsKits
				.filter(
					(kit) =>
						!kit.id.startsWith('stock_') &&
						(kit.vehicleModel === null || kit.vehicleModel === vehicleModel),
				)
				.map((kit) => ({
					value: kit.id,
					label: kit.name,
				}));
		},
		[gameConfigs],
	);

	const getTuningItemOptions = useCallback(
		(vehicleModel: string) => {
			if (!gameConfigs?.vehicleTuning) return [];
			return gameConfigs.vehicleTuning.items
				.filter(
					(item) =>
						!item.id.startsWith('stock_') &&
						(item.vehicleModel === null || item.vehicleModel === vehicleModel),
				)
				.map((item) => ({
					value: item.id,
					label: item.name,
					category: item.shopCategory ?? undefined,
				}));
		},
		[gameConfigs],
	);

	const tuningKitAllOptions = useMemo(() => {
		if (!gameConfigs?.vehicleTuning) return [];
		return gameConfigs.vehicleTuning.itemsKits
			.filter((kit) => !kit.id.startsWith('stock_'))
			.map((kit) => ({
				value: kit.id,
				label: kit.name,
			}));
	}, [gameConfigs]);

	const tuningItemAllOptions = useMemo(() => {
		if (!gameConfigs?.vehicleTuning) return [];
		return gameConfigs.vehicleTuning.items
			.filter((item) => !item.id.startsWith('stock_'))
			.map((item) => ({
				value: item.id,
				label: item.name,
			}));
	}, [gameConfigs]);

	const getOptionsForRewardType = useCallback(
		(type: RewardType): Option[] => {
			switch (type) {
				case RewardType.VEHICLE:
					return vehicleOptions;
				case RewardType.LOOTBOX:
					return lootboxOptions;
				case RewardType.BUNDLE:
					return bundleOptions;
				case RewardType.ITEM:
					return itemOptions;
				case RewardType.VEHICLE_TUNING_KIT:
					return tuningKitAllOptions;
				case RewardType.VEHICLE_TUNING_ITEM:
					return tuningItemAllOptions;
				default:
					return [];
			}
		},
		[
			vehicleOptions,
			lootboxOptions,
			bundleOptions,
			itemOptions,
			tuningKitAllOptions,
			tuningItemAllOptions,
		],
	);

	return {
		vehicleOptions,
		lootboxOptions,
		bundleOptions,
		itemOptions,
		getTuningKitOptions,
		getTuningItemOptions,
		getOptionsForRewardType,
	};
};
